import { z } from "zod";

import { timeFiltersSearchSchema } from "@/features/directory/utils/time-filters";
import { ServiceType, SpecialistProfession } from "@complete-athlete/enums";

/**
 * What a search is, as it lives in the address. Everything is a query parameter of `/search`, so
 * any search is a link someone can send, and a broken or stale value is dropped rather than failing
 * the page. A plain service or city moves to an address of its own (`search-path.ts`, ADR 0042).
 */
export const directorySearchSchema = z.object({
  /** A catalogue type, by its English slug. */
  service: z.enum(ServiceType).optional().catch(undefined),
  /** Free words, when nothing in the catalogue was picked: a name, a business, a phrase. */
  q: z.string().trim().max(80).optional().catch(undefined),
  /** A city as typed or picked, matched without case or Polish letters. */
  city: z.string().trim().max(80).optional().catch(undefined),
  /** Near a point the visitor shared, as `lat,lng` rounded to about 100 m. */
  near: z
    .string()
    .regex(/^-?\d{1,2}\.\d{1,4},-?\d{1,3}\.\d{1,4}$/)
    .optional()
    .catch(undefined),
  /** Only what is held over video. */
  online: z.literal(true).optional().catch(undefined),
});

export type DirectorySearch = z.infer<typeof directorySearchSchema>;

export const DIRECTORY_SORTS = ["relevance", "distance", "price"] as const;
export type DirectorySort = (typeof DIRECTORY_SORTS)[number];

/**
 * The specialists page's address: the same search, and the filters it adds (ADR 0036). So a
 * filtered list is a link too, and the back button walks through what was tried.
 */
export const specialistsSearchSchema = directorySearchSchema.extend(timeFiltersSearchSchema.shape).extend({
  /** The saved people instead of everyone. `events` is only the events page's old address. */
  tab: z.enum(["saved", "events"]).optional().catch(undefined),
  profession: z.enum(SpecialistProfession).optional().catch(undefined),
  /** The most it may cost, in whole currency units, as read on the slider. */
  priceMax: z.number().int().positive().optional().catch(undefined),
  sort: z.enum(DIRECTORY_SORTS).optional().catch(undefined),
});

export type SpecialistsSearch = z.infer<typeof specialistsSearchSchema>;

/** A search as the API takes it: strings for the query string, and defaults left out. */
export const apiQuery = (search: SpecialistsSearch, page = 1) => ({
  ...(search.service ? { service: search.service } : {}),
  ...(search.q ? { q: search.q } : {}),
  ...(search.city ? { city: search.city } : {}),
  ...(search.near ? { near: search.near } : {}),
  ...(search.online ? { online: "true" as const } : {}),
  ...(search.profession ? { profession: search.profession } : {}),
  ...(search.priceMax ? { priceMax: String(search.priceMax * 100) } : {}),
  ...(search.when ? { when: search.when } : {}),
  ...(search.when === "date" && search.date ? { date: search.date } : {}),
  ...(search.parts?.length ? { parts: search.parts.join(",") } : {}),
  ...(search.instant ? { instant: "true" as const } : {}),
  sort: search.sort ?? "relevance",
  page: String(page),
});

/** A shared position, rounded so the address never pinpoints anyone's home. */
export const nearFrom = ({ latitude, longitude }: { latitude: number; longitude: number }) =>
  `${latitude.toFixed(3)},${longitude.toFixed(3)}`;
